// @flow
import instadate from 'instadate';

import api, { normalizeData, sleep } from '../../configs/api-generators.js';
import { setLoadingMessage, setMessages, setModal, setProgress } from '../ui/ui.actions.js';

import {
  ADD_COMMENT,
  AIR_EVENT_TYPE,
  CANCEL_CHECKIN,
  CANCEL_CHECKOUT,
  CHANGE_STATUS,
  CHECK_EVENT_PARAMS,
  defaultFilterForm,
  DELETE_DISCREPANCY,
  DISCREPANCY_ADD,
  DISCREPANCY_GET,
  EDIT_COMMENT,
  EVENT_ADD,
  EVENT_ADD_TO_WAITING,
  EVENT_CANCEL,
  EVENT_CHECKIN,
  EVENT_CHECKIN_START,
  EVENT_CHECKOUT,
  EVENT_CHECKOUT_START,
  EVENT_DELETE,
  EVENT_EDIT,
  EVENT_RECURRING,
  EVENT_SAVE,
  EVENT_VIEW,
  GET_AIRCRAFT,
  GET_TIMEZONE,
  MAINTENANCE_EVENT_TYPE,
  MOVE_EVENT,
  MOVE_EVENT_FAILED,
  MOVE_EVENT_FINISH,
  MOVE_EVENT_FINISH_FAILED,
  MOVE_EVENT_RECURRENT,
  MOVE_EVENT_START,
  MOVE_EVENT_START_FAILED,
  SAVE_COUNTER,
  SAVE_DISCREPANCY,
  SLICE_BREADCRUMBS,
  USERS_FILTER,
  USERS_SETTINGS
} from './calendar.consts.js';

import {
  concatMessages,
  getSimpleValue,
  getTimestamp,
  int,
  newDate,
  paramsToHash,
  redirectWithParams
} from './calendar.model.js';

import { extractMessages } from '../ui/ui.helpers.js';

export function sliceBreadcrumbs(num: ?number = false) {
  return {
    type: SLICE_BREADCRUMBS,
    payload: { num }
  };
}

//TODO make this in more elegant way
const weightAndBalanceErrors = [
  'Ramp CG out of limits',
  'Takeoff CG out of limits',
  'Landing CG out of limits',
  'Maximum fuel load is below minimum required'
];

function isUserInstructor(event, userID, resources) {
  const instructorResource = resources[event.instructorID];
  return instructorResource ? instructorResource.userID === userID : false;
}

export function* addFilter(): Yield<*> {
  try {
    yield setProgress(true, USERS_FILTER);
    yield setLoadingMessage();

    const { filterForm, URL, timezone, organization } = yield s => s;

    const response = yield* api('/users/addFilter/', {
      filterName: filterForm.name,
      filter: {
        _action: URL.action,
        _timezone: timezone.id,
        organizationUnique: organization.unique,
        ...getSimpleValue(filterForm)
      }
    });

    if (response.ok) {
      yield setMessages(extractMessages(response));
      yield setModal(false, USERS_FILTER);
      yield setProgress(false, USERS_FILTER);
      return redirectWithParams(
        URL,
        {
          timezone: timezone.id,
          ...response.data.filter,
          id: response.data.id
        },
        response.data
      );
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, USERS_FILTER);
    return setMessages({
      type: 'error',
      text: e.message
    });
  }
}

export function* saveFilter(): Yield<*> {
  try {
    yield setProgress(true, USERS_FILTER);

    const { filterForm, URL, timezone, organization } = yield s => s;

    const response = yield* api(`/users/saveFilter/${filterForm.id}/`, {
      filterName: filterForm.name,
      filter: {
        _action: URL.action,
        _timezone: timezone.id,
        organizationUnique: organization.unique,
        ...getSimpleValue(filterForm)
      }
    });
    if (response.ok) {
      yield setMessages(extractMessages(response));
      yield setProgress(false, USERS_FILTER);
      return redirectWithParams(
        URL,
        {
          timezone: timezone.id,
          ...response.data.filter,
          id: response.data.id
        },
        response.data
      );
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, USERS_FILTER);
    return setMessages({
      type: 'error',
      text: e.message
    });
  }
}

export function* deleteFilter(): Yield<*> {
  try {
    yield setProgress(true, USERS_FILTER);

    const filterForm = yield s => s.filterForm;
    const URL = yield s => s.URL;

    const response = yield* api(`/users/deleteFilter/${filterForm.id}/`, {});
    if (response.ok) {
      yield setMessages(extractMessages(response));
      yield setProgress(false, USERS_FILTER);
      return redirectWithParams(URL, { filter: '', ...defaultFilterForm });
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, USERS_FILTER);
    return setMessages({
      type: 'error',
      text: e.message
    });
  }
}

export function* saveUserSetting(futureDayLimit: string): Yield<*> {
  try {
    yield setProgress(true, USERS_SETTINGS);

    const response = yield* api('/usersettings/saveUserSetting/', {
      name: 'futureDayLimit',
      value: futureDayLimit
    });

    if (response.ok) {
      yield setProgress(false, USERS_SETTINGS);
      return window.location.reload();
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, USERS_SETTINGS);
    return setMessages({
      type: 'error',
      text: e.message
    });
  }
}

export function* getTimeZone(timeZoneID: string, time: number): Yield<*> {
  try {
    const response = yield* api('/calendar/getTimeZone/', {
      timeZoneIDs: [timeZoneID],
      time
    });

    if (response.ok) {
      yield { type: GET_TIMEZONE, payload: response.data[timeZoneID] };
      return true;
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    return setMessages({
      type: 'error',
      text: e.message
    });
  }
}

function prepareEventRequestData(
  { URL, eventForm, timezones, locations, timezone, organization, accessgroups },
  eventDefaultLength
) {
  const { offset } = timezone;
  const locOffset = eventForm.timezone.offset;

  const pilotOptional =
    organization.organizationSettings.pilotOptional && accessgroups.calendarManageAnothers ? 1 : 0;

  return {
    ...eventForm,
    // Ensure pilotID is "0" if not set, keep as is otherwise
    pilotID:
      eventForm.pilotID && parseInt(eventForm.pilotID, 10) > 0
        ? eventForm.pilotID
        : "0",
    // Ensure secondInComandID is null if not set or 0
    secondInComandID:
      eventForm.secondInComandID && parseInt(eventForm.secondInComandID, 10) > 0
        ? eventForm.secondInComandID
        : null,
    // Ensure instructorID is null or empty string, not undefined or 0
    instructorID:
      eventForm.instructorID && parseInt(eventForm.instructorID, 10) > 0
        ? eventForm.instructorID
        : null,
    timezone: null,
    startTime: newDate(URL.eventParams.startTime, locOffset, true),
    finishTime: newDate(
      URL.eventParams.finishTime ||
        parseInt(URL.eventParams.startTime, 10) + parseInt(eventDefaultLength, 10),
      locOffset,
      true
    ),
    pilotOptional,
    recurringEndDate:
      eventForm.recurringEndDate &&
      eventForm.recurringEndDate.toString().length &&
      newDate(eventForm.recurringEndDate, locOffset, false),
    globalStartTime: newDate(URL.eventParams.startTime, offset, true),
    globalFinishTime: newDate(
      URL.eventParams.finishTime ||
        parseInt(URL.eventParams.startTime, 10) + parseInt(eventDefaultLength, 10),
      offset,
      true
    )
  };
}

export function* checkEventParams(
  needToGetTimezone?: boolean,
  locationID?: string,
  newOrgUnique?: string
): Yield<*> {
  try {
    yield setProgress(true, CHECK_EVENT_PARAMS);

    let { eventForm } = yield s => s;
    const { URL, locations, user, organization, accessgroups } = yield s => s;

    const noDefaultPilot =
      accessgroups.calendarManageAnothers &&
      organization.organizationSettings.pilotOptional === '1';

    const timeZoneID =
      (locations[locationID] && locations[locationID].timeZoneID) ||
      (locations[URL.eventParams.location] && locations[URL.eventParams.location].timeZoneID) ||
      (locations[eventForm.locationID] && locations[eventForm.locationID].timeZoneID);

    if (needToGetTimezone) {
      if (!timeZoneID) {
        yield { type: CHECK_EVENT_PARAMS, payload: {} };
        throw new Error("The organization doesn't have any locations");
      }
      yield* getTimeZone(timeZoneID, eventForm.startTime);
    }

    eventForm = yield s => s.eventForm;
    if (!eventForm.locationID) {
      yield { type: CHECK_EVENT_PARAMS, payload: {} };
      return setProgress(false, CHECK_EVENT_PARAMS);
    }

    const payload = yield s => prepareEventRequestData(s, user.eventDefaultLength);

    const withId =
      eventForm.id &&
      (URL.eventParams.action === EVENT_EDIT || URL.eventParams.action === EVENT_RECURRING);

    const response = yield* api(
      `/org/${newOrgUnique || URL.eventParams.org}/calendar/checkEventParams/${
        withId ? `${eventForm.id}/` : ''
      }`,
      payload
    );

    if (response.data) {
      const messages = concatMessages(response, 'error', URL.eventParams.action, Infinity);

      if (response.error && response.error.length) {
        response.error.forEach(error => {
          messages.push({
            type: 'error',
            text: error,
            timeout: 60 * 1000 * 20
          });
        });
      }

      yield setMessages([...messages, ...extractMessages(response, URL.eventParams.action)]);

      const { resources, resourcesList } = normalizeData(
        response.data.resources,
        'resources',
        'resourcesList'
      );

      const addToWaiting = eventForm.resources.find(
        resID => parseInt(resources[resID] && resources[resID].addToWaiting, 10) === 1
      );
      yield {
        type: EVENT_ADD_TO_WAITING,
        payload: {
          addToWaiting: addToWaiting ? '1' : '0'
        }
      };

      const defaultPilot =
        !noDefaultPilot &&
        response.data &&
        response.data.users &&
        response.data.users.pilots &&
        response.data.users.pilots.length &&
        response.data.users.pilots.find(pilot => pilot.pilotID === user.id);

      yield {
        type: CHECK_EVENT_PARAMS,
        payload: {
          ...response.data,
          resources,
          resourcesList,
          pilotID: defaultPilot ? defaultPilot.pilotID : null
        }
      };

      if (defaultPilot && !eventForm.pilotID && defaultPilot.pilotID !== eventForm.pilotID) {
        yield* checkEventParams();
      }

      return setProgress(false, CHECK_EVENT_PARAMS);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    const { URL } = yield s => s;
    yield setProgress(false, CHECK_EVENT_PARAMS);
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* addEvent(): Yield<*> {
  try {
    yield setProgress(true, EVENT_ADD);

    const { URL, user, eventForm, userUI, resources } = yield s => s;
    const payload = yield s => prepareEventRequestData(s, user.eventDefaultLength);

    const subRoute =
      user.id === eventForm.pilotID || isUserInstructor(eventForm, user.id, resources)
        ? 'addMyEvent'
        : 'addEvent';

    const response = yield* api(`/org/${URL.eventParams.org}/calendar/${subRoute}/`, payload);

    if (response.ok) {
      yield setProgress(false, EVENT_ADD);
      yield { type: EVENT_ADD, payload: response.data };
      yield paramsToHash({}, userUI.scrollY);
      return setMessages(extractMessages(response));
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, EVENT_ADD);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* saveEvent(requestBody): Yield<*> {
  try {
    yield setProgress(true, EVENT_SAVE);

    const { URL, user, eventForm, userUI, resources } = yield s => s;
    const payload = yield s => prepareEventRequestData(s, user.eventDefaultLength);

    const subRoute =
      user.id === eventForm.pilotID || isUserInstructor(eventForm, user.id, resources)
        ? 'saveMyEvent'
        : 'saveEvent';

    const response = yield* api(
      `/org/${URL.eventParams.org}/calendar/${subRoute}/${URL.eventParams.event}/`,
      {
        ...payload,
        ...(requestBody || null)
      }
    );

    if (response.ok) {
      yield setProgress(false, EVENT_SAVE);
      yield { type: EVENT_SAVE, payload: response.data };
      yield paramsToHash({}, userUI.scrollY);
      // Done this in HW-247
      const messages = extractMessages(response).filter(message => {
        return !weightAndBalanceErrors.includes(message.text);
      });

      return setMessages(messages);
    }

    if (!response.ok && !response.success) {
      yield setProgress(false, EVENT_SAVE);
      return setMessages([
        {
          type: 'error',
          text: response.error
        }
      ]);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    const { URL } = yield s => s;
    yield setProgress(false, EVENT_SAVE);
    return setMessages([
      {
        type: 'error',
        text: e.message,
        timeout: Infinity,
        group: URL.eventParams.action
      }
    ]);
  }
}

export function* addMaintenance(): Yield<*> {
  try {
    yield setProgress(true, EVENT_ADD);

    const { URL, eventForm, timezone, userUI, user } = yield s => s;
    const payload = yield s => prepareEventRequestData(s, user.eventDefaultLength);

    const response = yield* api(
      `/org/${URL.eventParams.org}/resources/addMaintenance/${eventForm.resources[0]}/`,
      {
        startTime: payload.startTime,
        finishTime: payload.finishTime,
        comments: eventForm.comments,
        _timezone: timezone.id
      }
    );

    if (response.ok) {
      yield setProgress(false, EVENT_ADD);
      yield { type: EVENT_ADD, payload: response.data };
      yield paramsToHash({}, userUI.scrollY);
      return setMessages(extractMessages(response));
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, EVENT_ADD);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* saveMaintenance(): Yield<*> {
  try {
    yield setProgress(true, EVENT_SAVE);

    const { URL, eventForm, timezone, userUI, user } = yield s => s;
    const payload = yield s => prepareEventRequestData(s, user.eventDefaultLength);

    const response = yield* api(
      `/org/${URL.eventParams.org}/resources/saveMaintenance/${eventForm.resources[0]}/`,
      {
        startTime: payload.startTime,
        finishTime: payload.finishTime,
        maintenanceID: eventForm.id,
        comments: eventForm.comments,
        _timezone: timezone.id
      }
    );

    if (response.ok) {
      yield setProgress(false, EVENT_SAVE);
      yield { type: EVENT_SAVE, payload: response.data };
      yield paramsToHash({}, userUI.scrollY);
      return setMessages(extractMessages(response));
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, EVENT_SAVE);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* getEvent(id): Yield<*> {
  try {
    yield setProgress(true, EVENT_VIEW);
    yield setMessages([]);

    const { URL, timezone } = yield s => s;

    const response = yield* api(
      `/org/${URL.eventParams.org}/calendar/event/${id || URL.eventParams.event}/`,
      {
        actionName: URL.action !== 'dispatch' ? URL.action : 'day',
        _timezone: timezone.id
      }
    );

    if (response.ok) {
      yield { type: EVENT_VIEW, payload: response.data.event };
      yield setProgress(false, EVENT_VIEW);
      const messages = extractMessages(response, EVENT_VIEW, 5000).filter(message => {
        return message.type !== 'warning';
      });
      yield setMessages(messages);
      return true;
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, EVENT_VIEW);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* deleteEvent(type: string = ''): Yield<*> {
  try {
    yield setProgress(true, EVENT_DELETE);

    const { URL, timezone, event, userUI } = yield s => s;
    const resource = event.resources[0] ? event.resources[0].id : event.instructorID;

    const response = yield* api(
      event.type === AIR_EVENT_TYPE
        ? `/org/${URL.eventParams.org}/calendar/deleteEvent/${URL.eventParams.event}/`
        : `/org/${URL.eventParams.org}/resources/deleteMaintenance/${resource}/`,
      {
        actionName: URL.action,
        _timezone: timezone.id,
        eventsForAction: type,
        ...(event.type === AIR_EVENT_TYPE ? {} : { maintenanceID: URL.eventParams.event })
      }
    );

    if (response.ok) {
      yield setProgress(false, EVENT_DELETE);
      yield { type: EVENT_DELETE, payload: response.data };
      yield paramsToHash({}, userUI.scrollY);
      return setMessages(extractMessages(response));
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, EVENT_DELETE);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

// ---------- CANCEL ---------- //
export function* cancelEvent(): Yield<*> {
  try {
    yield setProgress(true, EVENT_CANCEL);

    const { URL, timezone, user, event, userUI, resources } = yield s => s;
    const { params } = yield s => s.eventForm;
    const { id, organizationUnique, pilotID, reason, textReason, eventsForAction = '' } = event;

    const subRoute =
      user.id === event.pilotID || isUserInstructor(event, user.id, resources)
        ? 'cancelMyEvent'
        : 'cancelEvent';

    const response = yield* api(
      `/org/${URL.eventParams.org}/calendar/${subRoute}/${URL.eventParams.event}/`,
      {
        id,
        organizationUnique,
        pilotID,
        reason: reason !== 'other' ? reason : textReason,
        eventsForAction,
        actionName: URL.action,
        _timezone: timezone.id,
        params
      }
    );

    if (response.ok) {
      yield setProgress(false, EVENT_CANCEL);
      yield { type: EVENT_CANCEL, payload: response.data };
      yield paramsToHash({}, userUI.scrollY);
      return setMessages(extractMessages(response));
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, EVENT_CANCEL);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* cancelCheckOut(): Yield<*> {
  try {
    yield setProgress(true, CANCEL_CHECKOUT);

    const { URL, timezone } = yield s => s;

    const response = yield* api(
      `/org/${URL.eventParams.org}/calendar/cancelCheckOut/${URL.eventParams.event}/`,
      {
        actionName: URL.action,
        _timezone: timezone.id
      }
    );

    if (response.ok) {
      yield setProgress(false, CANCEL_CHECKOUT);
      yield { type: CANCEL_CHECKOUT, payload: URL.eventParams.event };
      yield* getEvent();

      return setMessages(extractMessages(response, URL.eventParams.action));
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, CANCEL_CHECKOUT);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* cancelCheckIn(): Yield<*> {
  try {
    yield setProgress(true, CANCEL_CHECKIN);

    const { URL, timezone } = yield s => s;

    const response = yield* api(
      `/org/${URL.eventParams.org}/calendar/cancelCheckIn/${URL.eventParams.event}/`,
      {
        actionName: URL.action,
        _timezone: timezone.id
      }
    );

    if (response.ok) {
      yield setProgress(false, CANCEL_CHECKIN);
      yield { type: CANCEL_CHECKIN, payload: URL.eventParams.event };
      yield* getEvent();

      return setMessages(extractMessages(response));
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, CANCEL_CHECKIN);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

// ---------- CHECKOUT ---------- //
export function* checkOutStart(): Yield<*> {
  try {
    yield setProgress(true, EVENT_CHECKOUT_START);

    const { URL, timezone } = yield s => s;

    const response = yield* api(
      `/org/${URL.eventParams.org}/calendar/checkOutStart/${URL.eventParams.event}/`,
      {
        actionName: URL.action,
        _timezone: timezone.id
      }
    );

    if (response.ok) {
      yield {
        type: EVENT_CHECKOUT_START,
        payload: {
          ...response.data,
          ...normalizeData(response.data.counters, 'counters', 'countersList')
        }
      };
      return setProgress(false, EVENT_CHECKOUT_START);
    }
    if (!response.error) return false;

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, EVENT_CHECKOUT_START);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* saveCounter(counterID: string): Yield<*> {
  try {
    yield setProgress(true, SAVE_COUNTER);

    const { URL, timezone, checkoutForm } = yield s => s;

    const response = yield* api(
      `/org/${URL.eventParams.org}/hangars/save${
        checkoutForm.aircraft.simulatorID ? 'Simulator' : ''
      }Counter/`,
      {
        ...checkoutForm.counters[counterID],
        actionName: URL.action,
        _timezone: timezone.id
      }
    );

    if (response.ok) {
      yield setProgress(false, SAVE_COUNTER);
      return setMessages(extractMessages(response, URL.eventParams.action));
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, SAVE_COUNTER);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* checkOut(data): Yield<*> {
  try {
    yield setProgress(true, EVENT_CHECKOUT);
    const { URL, timezone, userUI } = yield s => s;
    const payload = {
      actionName: URL.action,
      _timezone: timezone.id
    };

    if (data) {
      payload.departureTime = data.departureTime;
      payload.flightTime = data.flightTime;
      payload.expectedArrivalTime = data.expectedArrivalTime;
      payload.timeZoneId = data.timeZoneId;
    }

    const response = yield* api(
      `/org/${URL.eventParams.org}/calendar/checkOutFinish/${URL.eventParams.event}/`,
      payload
    );

    if (response.ok) {
      yield setProgress(false, EVENT_CHECKOUT);
      yield paramsToHash({ ...URL.eventParams, action: EVENT_VIEW }, userUI.scrollY);
      yield sliceBreadcrumbs(1);

      yield { type: EVENT_CHECKOUT, payload: URL.eventParams.event };
      return setMessages(extractMessages(response, EVENT_VIEW));
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, EVENT_CHECKOUT);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

// ---------- DISCREPANCY ---------- //
export function* getAircraft(): Yield<*> {
  try {
    yield setProgress(true, GET_AIRCRAFT);

    const { URL } = yield s => s;

    const response = yield* api(
      `/org/${URL.eventParams.org}/calendar/getAircraft/${URL.eventParams.element}/`,
      {}
    );

    if (response.ok) {
      yield setProgress(false, GET_AIRCRAFT);
      yield {
        type: GET_AIRCRAFT,
        payload: {
          ...response.data,
          ...normalizeData(response.data.discrepancies, 'discrepancies', 'discrepanciesList')
        }
      };
      return setProgress(false, GET_AIRCRAFT);
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, GET_AIRCRAFT);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* getSimulator(): Yield<*> {
  try {
    yield setProgress(true, GET_AIRCRAFT);

    const { URL } = yield s => s;

    const response = yield* api(
      `/org/${URL.eventParams.org}/calendar/getSimulator/${URL.eventParams.element}/`,
      {}
    );

    if (response.ok) {
      yield {
        type: GET_AIRCRAFT,
        payload: response.data
      };
      return setProgress(false, GET_AIRCRAFT);
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, GET_AIRCRAFT);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* changeStatus(id: string, status: string): Yield<*> {
  try {
    yield setProgress(true, CHANGE_STATUS);

    const { URL, aircraft, UI } = yield s => s;

    const response = yield* api(`/org/${URL.eventParams.org}/discrepancies/changeStatus/${id}/`, {
      status,
      disposition: aircraft.discrepancies[id].disposition || '',
      logbookEntry: aircraft.discrepancies[id].logbookEntry || ''
    });

    if (response.ok) {
      yield {
        type: CHANGE_STATUS,
        payload: {
          status,
          id,
          ...response.data
        }
      };
      yield setMessages(extractMessages(response));

      const activeDiscrepancyEdit = Object.keys(UI.modals).find(key => key.includes('DISCR-EDIT-'));
      if (activeDiscrepancyEdit) yield setModal(false, activeDiscrepancyEdit);

      return setProgress(false, CHANGE_STATUS);
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, CHANGE_STATUS);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* editComment(id: string, text: string): Yield<*> {
  try {
    yield setProgress(true, EDIT_COMMENT);

    const { URL } = yield s => s;

    const response = yield* api(`/org/${URL.eventParams.org}/discrepancies/editComment/${id}/`, {
      disposition: text
    });

    if (response.ok) {
      yield {
        type: EDIT_COMMENT,
        payload: response.data.activities[0]
      };
      yield setMessages(extractMessages(response, URL.eventParams.action));
      yield setModal(false, `edit-comment-${id}`);
      return setProgress(false, EDIT_COMMENT);
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, EDIT_COMMENT);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* addComment(): Yield<*> {
  try {
    yield setProgress(true, ADD_COMMENT);

    const { URL, discrepancy } = yield s => s;

    const response = yield* api(
      `/org/${URL.eventParams.org}/discrepancies/comment/${URL.eventParams.discrepancyid}/`,
      {
        disposition: discrepancy.disposition
      }
    );

    if (response.ok) {
      yield {
        type: ADD_COMMENT,
        payload: response.data
      };
      yield setMessages(extractMessages(response, URL.eventParams.action));
      yield setModal(false, 'discrepancyComment');
      return setProgress(false, ADD_COMMENT);
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, ADD_COMMENT);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* saveDiscrepancy(id: string, payload): Yield<*> {
  try {
    yield setProgress(true, SAVE_DISCREPANCY);

    const { URL, aircraft, UI } = yield s => s;

    const response = yield* api(`/org/${URL.eventParams.org}/discrepancies/save/${id}/`, {
      ...aircraft.discrepancies[id],
      ...payload
    });

    if (response.ok) {
      yield {
        type: SAVE_DISCREPANCY,
        payload: response.data
      };
      yield setMessages(extractMessages(response));

      const activeDiscrepancyEdit = Object.keys(UI.modals).find(key => key.includes('DISCR-EDIT-'));
      if (activeDiscrepancyEdit) yield setModal(false, activeDiscrepancyEdit);

      return setProgress(false, SAVE_DISCREPANCY);
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, SAVE_DISCREPANCY);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* deleteDiscrepancy(): Yield<*> {
  try {
    yield setProgress(true, DELETE_DISCREPANCY);

    const { URL, userUI } = yield s => s;
    const response = yield* api(
      `/org/${URL.eventParams.org}/discrepancies/delete/${URL.eventParams.discrepancyid}/`,
      {}
    );

    if (response.ok) {
      yield {
        type: DELETE_DISCREPANCY,
        payload: URL.eventParams.discrepancyid
      };
      yield setMessages(extractMessages(response));
      yield paramsToHash(
        {
          ...URL.eventParams,
          action: URL.eventBreadcrumbs[URL.eventBreadcrumbs.length - 2].action
        },
        userUI.scrollY
      );
      yield sliceBreadcrumbs(1);

      return setProgress(false, DELETE_DISCREPANCY);
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, DELETE_DISCREPANCY);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* addDiscrepancy(): Yield<*> {
  try {
    yield setProgress(true, DISCREPANCY_ADD);

    const { URL, discrepancyForm, userUI } = yield s => s;

    const response = yield* api(`/org/${URL.eventParams.org}/discrepancies/add/`, {
      ...discrepancyForm,
      aircraftID: URL.eventParams.element
    });

    if (response.ok) {
      yield {
        type: DISCREPANCY_ADD,
        payload: response.data
      };
      yield setMessages(extractMessages(response));
      yield paramsToHash(
        {
          ...URL.eventParams,
          action: URL.eventBreadcrumbs[URL.eventBreadcrumbs.length - 2].action
        },
        userUI.scrollY
      );
      yield sliceBreadcrumbs(1);

      return setProgress(false, DISCREPANCY_ADD);
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, DISCREPANCY_ADD);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

export function* getDiscrepancy(): Yield<*> {
  try {
    yield setProgress(true, DISCREPANCY_GET);

    const { URL } = yield s => s;

    const response = yield* api(
      `/org/${URL.eventParams.org}/discrepancies/getDiscrepancy/${URL.eventParams.discrepancyid}/`,
      {}
    );

    if (response.ok) {
      yield {
        type: DISCREPANCY_GET,
        payload: response.data
      };
      return setProgress(false, DISCREPANCY_GET);
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    const { URL, userUI } = yield s => s;

    yield setProgress(false, DISCREPANCY_GET);
    yield paramsToHash(
      {
        ...URL.eventParams,
        action: EVENT_VIEW
      },
      userUI.scrollY
    );
    return sliceBreadcrumbs(1);
  }
}

// ---------- CHECKIN ---------- //
export function* checkInStart(): Yield<*> {
  try {
    yield setProgress(true, EVENT_CHECKIN_START);

    const { URL, timezone, customFields, events, userUI } = yield s => s;
    const response = yield* api(
      `/org/${URL.eventParams.org}/calendar/checkInStart/${URL.eventParams.event}/`,
      {
        _timezone: timezone.id
      }
    );

    if (response.ok) {
      if (response.data.aircraft.aircraftID && URL.eventParams.element) {
        yield* getAircraft();
      } else {
        yield* getSimulator();
      }

      if (!events[URL.eventParams.event]) {
        yield paramsToHash(
          {
            ...URL.eventParams,
            action: EVENT_VIEW
          },
          userUI.scrollY
        );
        return sliceBreadcrumbs();
      }

      const normCustomFields = Object.keys(response.data.customFields).reduce((acc, id) => {
        let normField;
        if (response.data.customFields[id] && customFields[id].type === 'date') {
          normField = newDate(getTimestamp(response.data.customFields[id]));
        }
        return {
          ...acc,
          [id]: normField || response.data.customFields[id]
        };
      }, {});

      const { resources, resourcesList } = normalizeData(
        response.data.resources,
        'resources',
        'resourcesList'
      );

      const initialValue = (
        instadate.differenceInMinutes(
          newDate(events[URL.eventParams.event].startTime),
          newDate(events[URL.eventParams.event].finishTime)
        ) / 60
      ).toFixed(2);

      const defaultCounters = response.data.counters.map(counter => ({
        ...counter,
        newValue: counter.value
      }));

      yield {
        type: EVENT_CHECKIN_START,
        payload: {
          ...response.data,
          ...normalizeData(defaultCounters, 'counters', 'countersList'),
          resources: resourcesList.reduce(
            (acc, id) => ({
              ...acc,
              [id]: {
                ...resources[id],
                diffCounter: initialValue
              }
            }),
            {}
          ),
          resourcesList,
          customFields: {
            ...normCustomFields
          }
        }
      };
      return setProgress(false, EVENT_CHECKIN_START);
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, EVENT_CHECKIN_START);
    const { URL, userUI } = yield s => s;
    yield setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });

    yield sleep(3000);
    yield paramsToHash(
      {
        ...URL.eventParams,
        action: EVENT_VIEW
      },
      userUI.scrollY
    );
    return sliceBreadcrumbs();
  }
}

export function* checkIn(params): Yield<*> {
  try {
    yield setProgress(true, EVENT_CHECKIN);

    const { URL, checkinForm, userUI } = yield s => s;

    const response = yield* api(
      `/org/${URL.eventParams.org}/calendar/checkInFinish/${URL.eventParams.event}/`,
      {
        ...params,
        ...checkinForm,
        counters: checkinForm.countersList.map(id => checkinForm.counters[id]),
        aircraft: null
      }
    );

    if (response.ok) {
      yield {
        type: EVENT_CHECKIN,
        payload: URL.eventParams.event
      };
      yield setMessages(extractMessages(response));
      yield paramsToHash(
        {
          ...URL.eventParams,
          action: EVENT_VIEW
        },
        userUI.scrollY
      );
      yield sliceBreadcrumbs();

      return setProgress(false, EVENT_CHECKIN);
    }
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, EVENT_CHECKIN);
    const { URL } = yield s => s;
    return setMessages({
      type: 'error',
      text: e.message,
      group: URL.eventParams.action
    });
  }
}

// ---------- MOVE EVENT ---------- //
export function* moveEventStart(): Yield<*> {
  try {
    yield setProgress(true, MOVE_EVENT_START);
    yield setLoadingMessage();

    const { draggedEvent, user, events, daysList } = yield s => s;
    const event = events[draggedEvent.event];

    const nextStartTime = int(daysList[0]) + (draggedEvent.left / 2) * 3 * 60;
    const subRoute = user.id === event.pilotID ? 'moveMyEventStart' : 'moveEventStart';

    const response = yield* api(
      `/org/${event.organizationUnique}/calendar/${subRoute}/${event.id}/`,
      {
        startTime: nextStartTime
      }
    );

    if (response.ok) {
      yield {
        type: MOVE_EVENT_START,
        payload: response.data.toDays[0].event
      };
      yield setMessages(extractMessages(response));

      return setProgress(false, MOVE_EVENT_START);
    }

    yield {
      type: MOVE_EVENT_START_FAILED,
      payload: {}
    };
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, MOVE_EVENT_START);
    return setMessages({
      type: 'error',
      text: e.message
    });
  }
}

export function* moveEventFinish(): Yield<*> {
  try {
    yield setProgress(true, MOVE_EVENT_FINISH);
    yield setLoadingMessage();

    const { draggedEvent, user, events, daysList } = yield s => s;
    const event = events[draggedEvent.event];
    const nextFinishTime = int(daysList[0]) + (draggedEvent.right / 2) * 3 * 60;
    const subRoute = user.id === event.pilotID ? 'moveMyEventFinish' : 'moveEventFinish';

    const response = yield* api(
      `/org/${event.organizationUnique}/calendar/${subRoute}/${event.id}/`,
      {
        finishTime: nextFinishTime
      }
    );

    if (response.ok) {
      yield {
        type: MOVE_EVENT_FINISH,
        payload: response.data.toDays[0].event
      };
      yield setMessages(extractMessages(response));

      return setProgress(false, MOVE_EVENT_FINISH);
    }

    yield {
      type: MOVE_EVENT_FINISH_FAILED,
      payload: {}
    };
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, MOVE_EVENT_FINISH);
    return setMessages({
      type: 'error',
      text: e.message
    });
  }
}

export function* moveEvent(): Yield<*> {
  try {
    yield setProgress(true, MOVE_EVENT);
    yield setModal(false, MOVE_EVENT_RECURRENT);
    yield setLoadingMessage();

    const { draggedEvent, user, events, daysList, timezone } = yield s => s;
    const event = events[draggedEvent.event];

    const nexStartTime = int(daysList[0]) + (draggedEvent.left / 2) * 3 * 60;
    const nextFinishTime = int(daysList[0]) + (draggedEvent.right / 2) * 3 * 60;

    let payload = {};
    const normNewResource = draggedEvent.newResource.split('-')[0];
    const normOldResource = draggedEvent.oldResource.split('-')[0];
    if (normNewResource && normNewResource !== normOldResource) {
      payload = {
        newResource: normNewResource,
        oldResource: normOldResource
      };
    }

    if (event.recurring === '1') {
      payload = {
        ...payload,
        eventsForAction: draggedEvent.eventsForAction
      };
    }

    const subRoute = user.id === event.pilotID ? 'moveMyEvent' : 'moveEvent';

    let url = `/org/${event.organizationUnique}/calendar/${subRoute}/${event.id}/`;

    if (event.type === MAINTENANCE_EVENT_TYPE) {
      url = `/org/${event.organizationUnique}/resources/moveMaintenance/${event.id}/`;
    }

    const response = yield* api(url, {
      ...payload,
      startTime: nexStartTime,
      finishTime: nextFinishTime,
      _timezone: timezone.id
    });

    if (response.ok) {
      yield {
        type: MOVE_EVENT,
        payload: {
          ...response.data,
          oldResource:
            normNewResource && normNewResource !== normOldResource ? normOldResource : null,
          newResource: normNewResource
        }
      };
      // Done this in HW-247
      const messages = extractMessages(response).filter(message => {
        return !weightAndBalanceErrors.includes(message.text);
      });

      yield setMessages(messages);

      return setProgress(false, MOVE_EVENT);
    }

    yield {
      type: MOVE_EVENT_FAILED,
      payload: {}
    };
    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setProgress(false, MOVE_EVENT);
    return setMessages([{
      type: 'error',
      text: e.message
    }]);
  }
}
